import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LOGOS } from "../constants/brand";
import { Mail, Lock, AlertCircle, ArrowLeft, Check } from "lucide-react";
import api from "../services/api";

function ForgotStep({ onSwitch }) {
  var emailState = useState("");
  var loadingState = useState(false);
  var sentState = useState(false);
  var errorState = useState(null);
  var navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!emailState[0].trim()) return;
    loadingState[1](true); errorState[1](null);
    try {
      await api.post("/auth/forgot-password", { email: emailState[0].trim() });
      sentState[1](true);
    } catch(err) {
      errorState[1](err.response?.data?.detail || "Erro ao solicitar recuperacao");
    } finally { loadingState[1](false); }
  }

  if (sentState[0]) {
    return (
      <div className="text-center" data-testid="forgot-success">
        <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-7 h-7 text-emerald-600" />
        </div>
        <h2 className="text-lg font-bold text-[#0A2F3D] mb-2">Solicitacao enviada!</h2>
        <p className="text-sm text-gray-500 mb-2">Se o email estiver cadastrado, voce recebera um link de recuperacao.</p>
        <p className="text-xs text-gray-400 mb-6">Verifique sua caixa de entrada e spam.</p>
        <div className="space-y-2">
          <button onClick={function() { onSwitch(); }} className="w-full py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="forgot-use-token">
            Ja tenho o codigo de recuperacao
          </button>
          <button onClick={function() { navigate("/login"); }} className="w-full py-2.5 text-sm text-[#3B82F6] hover:underline" data-testid="forgot-back-login">
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="forgot-form">
      <h2 className="text-lg font-bold text-[#0A2F3D] mb-1">Esqueceu sua senha?</h2>
      <p className="text-sm text-gray-500 mb-5">Informe seu email e enviaremos um link de recuperacao.</p>
      {errorState[0] && (
        <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl border border-red-100 mb-4">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-600">{errorState[0]}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
          <div className="relative">
            <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="email" value={emailState[0]} onChange={function(e) { emailState[1](e.target.value); }}
              placeholder="seu@email.com" required data-testid="forgot-email-input"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6]" />
          </div>
        </div>
        <button type="submit" disabled={loadingState[0]} data-testid="forgot-submit"
          className="w-full py-3 bg-[#3B82F6] text-white rounded-xl font-semibold hover:bg-[#2563EB] disabled:opacity-50 shadow-lg shadow-blue-500/25">
          {loadingState[0] ? "Enviando..." : "Enviar Link de Recuperacao"}
        </button>
      </form>
      <button onClick={function() { navigate("/login"); }} className="w-full flex items-center justify-center gap-1.5 mt-4 text-sm text-gray-400 hover:text-gray-600" data-testid="forgot-back">
        <ArrowLeft className="w-4 h-4" />Voltar ao login
      </button>
    </div>
  );
}

function ResetStep() {
  var searchParamsHook = useSearchParams();
  var searchParams = searchParamsHook[0];
  var tokenState = useState(searchParams.get("token") || "");
  var passwordState = useState("");
  var confirmState = useState("");
  var loadingState = useState(false);
  var successState = useState(false);
  var errorState = useState(null);
  var navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    errorState[1](null);
    if (passwordState[0].length < 6) { errorState[1]("Senha deve ter no minimo 6 caracteres"); return; }
    if (passwordState[0] !== confirmState[0]) { errorState[1]("Senhas nao conferem"); return; }
    if (!tokenState[0].trim()) { errorState[1]("Codigo de recuperacao obrigatorio"); return; }

    loadingState[1](true);
    try {
      await api.post("/auth/reset-password", { token: tokenState[0].trim(), new_password: passwordState[0] });
      successState[1](true);
    } catch(err) {
      errorState[1](err.response?.data?.detail || "Erro ao redefinir senha");
    } finally { loadingState[1](false); }
  }

  if (successState[0]) {
    return (
      <div className="text-center" data-testid="reset-success">
        <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-7 h-7 text-emerald-600" />
        </div>
        <h2 className="text-lg font-bold text-[#0A2F3D] mb-2">Senha redefinida!</h2>
        <p className="text-sm text-gray-500 mb-6">Voce ja pode fazer login com a nova senha.</p>
        <button onClick={function() { navigate("/login"); }} data-testid="reset-go-login"
          className="w-full py-3 bg-[#3B82F6] text-white rounded-xl font-semibold hover:bg-[#2563EB] shadow-lg shadow-blue-500/25">
          Ir para Login
        </button>
      </div>
    );
  }

  return (
    <div data-testid="reset-form">
      <h2 className="text-lg font-bold text-[#0A2F3D] mb-1">Redefinir Senha</h2>
      <p className="text-sm text-gray-500 mb-5">Insira o codigo recebido e crie uma nova senha.</p>
      {errorState[0] && (
        <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl border border-red-100 mb-4">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-600">{errorState[0]}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Codigo de Recuperacao</label>
          <input type="text" value={tokenState[0]} onChange={function(e) { tokenState[1](e.target.value); }}
            placeholder="Cole o codigo aqui" required data-testid="reset-token-input"
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6] font-mono" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Nova Senha</label>
          <div className="relative">
            <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="password" value={passwordState[0]} onChange={function(e) { passwordState[1](e.target.value); }}
              placeholder="Minimo 6 caracteres" required minLength={6} data-testid="reset-password-input"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6]" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirmar Nova Senha</label>
          <div className="relative">
            <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="password" value={confirmState[0]} onChange={function(e) { confirmState[1](e.target.value); }}
              placeholder="Repita a nova senha" required minLength={6} data-testid="reset-confirm-input"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6]" />
          </div>
        </div>
        <button type="submit" disabled={loadingState[0]} data-testid="reset-submit"
          className="w-full py-3 bg-[#3B82F6] text-white rounded-xl font-semibold hover:bg-[#2563EB] disabled:opacity-50 shadow-lg shadow-blue-500/25">
          {loadingState[0] ? "Redefinindo..." : "Redefinir Senha"}
        </button>
      </form>
      <button onClick={function() { navigate("/login"); }} className="w-full flex items-center justify-center gap-1.5 mt-4 text-sm text-gray-400 hover:text-gray-600" data-testid="reset-back">
        <ArrowLeft className="w-4 h-4" />Voltar ao login
      </button>
    </div>
  );
}

export default function ForgotPasswordPage() {
  var stepState = useState("forgot");

  return (
    <div className="min-h-screen bg-[#0A2F3D] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: "radial-gradient(circle at 25% 25%, #3B82F6 1px, transparent 1px), radial-gradient(circle at 75% 75%, #F26A4B 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }} />
      </div>
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGOS.WORDMARK} alt="Methos" className="h-10 sm:h-12 mx-auto mb-4 brightness-0 invert" style={{ clipPath: "inset(0 0 15% 0)" }} />
          <p className="text-white/50 text-sm">Recuperacao de senha</p>
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          {stepState[0] === "forgot" ? (
            <ForgotStep onSwitch={function() { stepState[1]("reset"); }} />
          ) : (
            <ResetStep />
          )}
        </div>
        <p className="text-center text-white/30 text-xs mt-6">Methos Corretora</p>
      </div>
    </div>
  );
}
